import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { DESIGNMATE } from "../../../../config/endPoints";
import { 
  EditOptionContainer, 
  EditOptionTitle, 
  EditOptionDescription, 
  FormGroup, 
  Button, 
  ButtonGroup,
  LoadingSpinner,
  SelectBox,
  TipBox,
  InputSlider,
  Switch,
  DragArea,
  ImagePreview
} from "../EditOptionStyles";
import { generateBackground } from "../../utils/editUtils";

const GenerateBackground = ({ onSubmit, isLoading, credits, setCredits, setShowPopup }) => {
  // Core settings
  const [isFastMode, setIsFastMode] = useState(true);
  const [backgroundType, setBackgroundType] = useState("prompt");
  const [numResults, setNumResults] = useState(1);
  
  // Prompt-based settings
  const [prompt, setPrompt] = useState("");
  const [negativePrompt, setNegativePrompt] = useState("");
  const [refinePrompt, setRefinePrompt] = useState(true);
  
  // Reference image settings
  const [refImageType, setRefImageType] = useState("url");
  const [refImageUrl, setRefImageUrl] = useState("");
  const [refImageFile, setRefImageFile] = useState(null);
  const [enhanceRefImage, setEnhanceRefImage] = useState(true);
  const [refImagePreviewUrl, setRefImagePreviewUrl] = useState(null);
  
  // Update prompt loading state
  const [isUpdatingPrompt, setIsUpdatingPrompt] = useState(false);
  
  // Background type options
  const backgroundOptions = [
    { value: "prompt", label: "AI Generated (Prompt)" },
    { value: "reference", label: "Reference Image" }
  ];
  
  // Number of results options
  const numResultsOptions = [
    { value: 1, label: "1" },
    { value: 2, label: "2" }
  ];

  /**
   * Function to update the prompt
   */
  const handleUpdatePrompt = async () => {
    try {
      if (!prompt) {
        return;
      }
      if (credits < 0.5) {
        setShowPopup && setShowPopup(true);
        return;
      }
      setIsUpdatingPrompt(true);
      const token = localStorage.getItem("authorization");
      const postOptions = {
        method: "POST",
        url: `${DESIGNMATE}/enhancePrompt`,
        headers: {
          accept: "application/json",
          authorization: token,
        },
        data: {
          prompt,
        },
      };
      const result = await axios(postOptions);
      if (result?.data?.responseMessage === "Insufficient Credits") {
        setShowPopup && setShowPopup(true);
        return;
      }
      setPrompt(result?.data?.enhancedPrompt);
      setCredits && setCredits(credits - 0.2);
    } catch (error) {
      console.log(error);
    } finally {
      setIsUpdatingPrompt(false);
    }
  };

  // Handle file upload
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setRefImageFile(file);
      
      // Generate preview URL
      const previewUrl = URL.createObjectURL(file);
      setRefImagePreviewUrl(previewUrl);
    }
  };

  // Cleanup preview URL when component unmounts or file changes
  useEffect(() => {
    return () => {
      if (refImagePreviewUrl) {
        URL.revokeObjectURL(refImagePreviewUrl);
      }
    };
  }, [refImagePreviewUrl]);

  // Function to handle file removal
  const handleRemoveFile = () => {
    if (refImagePreviewUrl) {
      URL.revokeObjectURL(refImagePreviewUrl);
      setRefImagePreviewUrl(null);
    }
    setRefImageFile(null);
  };

  // Handle form submission
  const handleSubmit = () => {
    // Create options object based on state
    const options = {
      fast: isFastMode,
      num_results: numResults
    };

    // Add options based on background type
    if (backgroundType === "prompt") {
      options.bg_prompt = prompt;
      options.refine_prompt = refinePrompt;
      
      // Add negative prompt only if fast mode is disabled
      if (!isFastMode && negativePrompt) {
        options.negative_prompt = negativePrompt;
      }
    } else if (backgroundType === "reference") {
      options.enhance_ref_image = enhanceRefImage;
      
      // Handle reference image based on type
      if (refImageType === "file" && refImageFile) {
        options.ref_image_file = refImageFile;
      } else if (refImageType === "url" && refImageUrl) {
        options.ref_image_url = refImageUrl;
      }
    }

    // Pass the editType, options, and apiFunction to the parent
    onSubmit({
      editType: "generateBackground", 
      options,
      apiFunction: generateBackground
    });
  };

  // Check if form can be submitted
  const canSubmit = () => {
    if (backgroundType === "prompt") {
      return prompt.trim() !== "";
    } else if (backgroundType === "reference") {
      return (refImageType === "url" && refImageUrl.trim() !== "") || 
             (refImageType === "file" && refImageFile !== null);
    }
    return false;
  };

  return (
    <EditOptionContainer>
      <div>
        <EditOptionTitle icon="🖼️">Generate Background</EditOptionTitle>
        <EditOptionDescription>
          Replace your image background with AI-generated content or use a reference image as inspiration.
        </EditOptionDescription>
      </div>

      <FormGroup label="Processing Mode">
        <div style={{ display: "flex", alignItems: "center" }}>
          <Switch 
            checked={isFastMode} 
            onChange={() => setIsFastMode(!isFastMode)}
            id="fast-mode-toggle"
          />
          <label 
            htmlFor="fast-mode-toggle" 
            style={{ 
              marginLeft: "0.5rem", 
              fontSize: "0.9rem", 
              color: "#555" 
            }}
          >
            {isFastMode ? "Fast Mode" : "Quality Mode"}
          </label>
        </div>
        <div style={{ 
          fontSize: "0.75rem", 
          color: "#888", 
          marginTop: "0.25rem" 
        }}>
          {isFastMode 
            ? "Optimized for speed with good results" 
            : "Highest quality results (takes longer to process)"}
        </div>
      </FormGroup>

      <FormGroup label="Background Type">
        <SelectBox 
          options={backgroundOptions} 
          value={backgroundType} 
          onChange={(e) => setBackgroundType(e.target.value)} 
        />
      </FormGroup>

      {backgroundType === "prompt" && (
        <>
          <FormGroup label="Background Description">
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="E.g., 'Beach sunset with palm trees' or 'Abstract blue bokeh'"
              style={{
                width: "100%",
                padding: "0.7rem",
                borderRadius: "6px",
                border: "1px solid #dde3ed",
                minHeight: "80px",
                resize: "vertical",
                outline: "none",
                fontSize: "0.9rem",
                fontFamily: "inherit",
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
              }}
            />
          </FormGroup>

          <FormGroup>
            <div style={{ 
              display: "flex", 
              justifyContent: "flex-end", 
              marginBottom: "1rem" 
            }}>
              <button
                type="button"
                disabled={!prompt.trim() || isUpdatingPrompt || (credits !== undefined && credits < 0.5)}
                onClick={handleUpdatePrompt}
                style={{
                  padding: "0.5rem 1rem",
                  backgroundColor: (!prompt.trim() || isUpdatingPrompt || (credits !== undefined && credits < 0.5)) ? "#ccc" : "MediumSeaGreen",
                  color: "#fff",
                  border: "none",
                  borderRadius: "5px",
                  cursor: (!prompt.trim() || isUpdatingPrompt || (credits !== undefined && credits < 0.5)) ? "not-allowed" : "pointer",
                  fontSize: "0.85rem",
                  fontWeight: "500"
                }}
              >
                {isUpdatingPrompt ? "Updating..." : "Update Prompt"}
              </button>
            </div>
          </FormGroup>

          <FormGroup>
            <div style={{ display: "flex", alignItems: "center" }}>
              <input 
                type="checkbox"
                checked={refinePrompt}
                onChange={() => setRefinePrompt(!refinePrompt)}
                id="refine-prompt-toggle"
                style={{
                  width: "16px",
                  height: "16px",
                  cursor: "pointer"
                }}
              />
              <label 
                htmlFor="refine-prompt-toggle" 
                style={{ 
                  marginLeft: "0.5rem", 
                  fontSize: "0.9rem", 
                  color: "#555",
                  cursor: "pointer"
                }}
              >
                Refine prompt for optimal results
              </label>
            </div>
          </FormGroup>

          {!isFastMode && (
            <FormGroup label="Negative Prompt (Optional)">
              <textarea
                value={negativePrompt}
                onChange={(e) => setNegativePrompt(e.target.value)}
                placeholder="Elements to avoid in the generated background, e.g., 'people, text, bright colors'"
                style={{
                  width: "100%",
                  padding: "0.7rem",
                  borderRadius: "6px",
                  border: "1px solid #dde3ed",
                  minHeight: "60px",
                  resize: "vertical",
                  outline: "none",
                  fontSize: "0.9rem",
                  fontFamily: "inherit",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
                }}
              />
            </FormGroup>
          )}
        </>
      )}

      {backgroundType === "reference" && (
        <>
          <FormGroup label="Reference Image Source">
            <div style={{
              display: "flex",
              gap: "1rem",
              marginBottom: "1rem"
            }}>
              <div 
                onClick={() => setRefImageType("url")}
                style={{
                  flex: 1,
                  padding: "0.75rem",
                  textAlign: "center",
                  borderRadius: "6px",
                  border: "1px solid #dde3ed",
                  backgroundColor: refImageType === "url" ? "#f0f5ff" : "white",
                  borderColor: refImageType === "url" ? "#4D7CFE" : "#dde3ed",
                  cursor: "pointer",
                  transition: "all 0.2s ease"
                }}
              >
                Image URL
              </div>
              <div 
                onClick={() => setRefImageType("file")}
                style={{
                  flex: 1,
                  padding: "0.75rem",
                  textAlign: "center",
                  borderRadius: "6px",
                  border: "1px solid #dde3ed",
                  backgroundColor: refImageType === "file" ? "#f0f5ff" : "white",
                  borderColor: refImageType === "file" ? "#4D7CFE" : "#dde3ed",
                  cursor: "pointer",
                  transition: "all 0.2s ease"
                }}
              >
                Upload File
              </div>
            </div>

            {refImageType === "url" ? (
              <input
                type="text"
                value={refImageUrl}
                onChange={(e) => setRefImageUrl(e.target.value)}
                placeholder="Enter URL of reference image"
                style={{
                  width: "100%",
                  padding: "0.7rem",
                  borderRadius: "6px",
                  border: "1px solid #dde3ed",
                  outline: "none",
                  fontSize: "0.9rem",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
                }}
              />
            ) : (
              <div>
                {refImageFile ? (
                  <div style={{ marginTop: '1rem' }}>
                    <ImagePreview 
                      src={refImagePreviewUrl}
                      onRemove={handleRemoveFile} 
                    />
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      marginTop: '0.5rem',
                      marginBottom: '1rem' 
                    }}>
                    
                    </div>
                  </div>
                ) : (
                  <DragArea
                    onDrop={(e) => {
                      e.preventDefault();
                      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                        const file = e.dataTransfer.files[0];
                        setRefImageFile(file);
                        const previewUrl = URL.createObjectURL(file);
                        setRefImagePreviewUrl(previewUrl);
                      }
                    }}
                    onDragOver={(e) => e.preventDefault()}
                    height={150}
                  >
                    <input
                      type="file"
                      id="reference-file-upload"
                      accept="image/*"
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                    />
                    <label 
                      htmlFor="reference-file-upload"
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: '100%',
                        cursor: 'pointer'
                      }}
                    >
                      <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🖼️</div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontWeight: '500', marginBottom: '0.5rem' }}>Drag & drop your reference image here</div>
                        <div style={{ 
                          fontSize: '0.85rem', 
                          color: '#888',
                          maxWidth: '80%',
                          margin: '0 auto'
                        }}>
                          or click to browse files
                        </div>
                      </div>
                    </label>
                  </DragArea>
                )}
              </div>
            )}
          </FormGroup>

          <FormGroup>
            <div style={{ display: "flex", alignItems: "center" }}>
              <input 
                type="checkbox"
                checked={enhanceRefImage}
                onChange={() => setEnhanceRefImage(!enhanceRefImage)}
                id="enhance-ref-toggle"
                style={{
                  width: "16px",
                  height: "16px",
                  cursor: "pointer"
                }}
              />
              <label 
                htmlFor="enhance-ref-toggle" 
                style={{ 
                  marginLeft: "0.5rem", 
                  fontSize: "0.9rem", 
                  color: "#555",
                  cursor: "pointer"
                }}
              >
                Enhance reference image for better results
              </label>
            </div>
          </FormGroup>
        </>
      )}

      <FormGroup label="Number of Results">
        <div 
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "8px",
            marginTop: "6px"
          }}
        >
          {numResultsOptions.map(option => (
            <div 
              key={option.value}
              onClick={() => setNumResults(option.value)}
              style={{
                flex: 1,
                padding: "10px 0",
                textAlign: "center",
                borderRadius: "6px",
                border: `1px solid ${numResults === option.value ? '#4D7CFE' : '#dde3ed'}`,
                backgroundColor: numResults === option.value ? '#f0f5ff' : 'white',
                cursor: "pointer",
                transition: "all 0.2s ease",
                fontWeight: numResults === option.value ? '600' : 'normal',
                fontSize: "0.9rem",
                color: numResults === option.value ? '#4D7CFE' : '#555',
                boxShadow: numResults === option.value ? '0 2px 4px rgba(77, 124, 254, 0.15)' : 'none'
              }}
            >
              {option.label}
            </div>
          ))}
        </div>
      </FormGroup>

      <TipBox 
        title="Pro Tip" 
        icon="💡"
      >
        {backgroundType === "prompt" 
          ? "Be specific in your prompt. Mention style, lighting, and mood for best results." 
          : "Choose a reference image with colors and lighting that complement your subject."}
      </TipBox>

      <ButtonGroup>
        <Button 
          primary={false} 
          outline={true}
          onClick={() => {
            setIsFastMode(true);
            setBackgroundType("prompt");
            setNumResults(1);
            setPrompt("");
            setNegativePrompt("");
            setRefinePrompt(true);
            setRefImageType("url");
            setRefImageUrl("");
            // Clean up existing URL object if present
            if (refImagePreviewUrl) {
              URL.revokeObjectURL(refImagePreviewUrl);
              setRefImagePreviewUrl(null);
            }
            setRefImageFile(null);
            setEnhanceRefImage(true);
          }}
        >
          Reset
        </Button>
        <Button 
          onClick={handleSubmit} 
          disabled={isLoading || !canSubmit()}
        >
          {isLoading ? (
            <>
              <LoadingSpinner />
              Processing...
            </>
          ) : (
            "Generate Background"
          )}
        </Button>
      </ButtonGroup>
    </EditOptionContainer>
  );
};

export default GenerateBackground;